import Leaflet from 'leaflet';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

import { showsLoading, showsUnloading } from '../mapDisplayKinds';
import { markerOpacity } from '../mapMarkerStyles';
import { MapRouteMarkerVM, MapVehicleRouteVM } from '../mapVM';
import { buildRouteMarkerEventHandlers } from '../markerEvents';

type Props = {
  routes: readonly MapVehicleRouteVM[];
  /** 地図に出す種類（積地 / 納品地 / 車庫）。番号マーカーは前 2 つで出し分ける */
  kinds: readonly string[];
  selectedRowIds: ReadonlySet<string>;
  /** 何か選ばれているか。選択が無いうちは全部濃いまま出す */
  hasSelection: boolean;
  onMarkerRowClick: (rowId: string) => void;
  onMarkerClick?: (rowId: string, marker: MapRouteMarkerVM) => void;
  /** 右クリックで選択解除（旧地図と同じ操作） */
  onMarkerRowDeselect?: (rowId: string) => void;
};

/**
 * 色は車両の設定から来る文字列で、`style` 属性へそのまま差し込むと `"` で属性を
 * 抜け出せる。CSS の色として読める文字だけ残す（残らなければ色が効かないだけ）。
 */
const cssColor = (color: string): string => color.replace(/[^#0-9a-zA-Z(),.%\s-]/g, '');

/**
 * 番号付き丸マーカーのアイコン。色と番号が同じなら使い回す。
 *
 * Why キャッシュ: 毎レンダーで新しい DivIcon を作ると、react-leaflet でも自前でも
 * 「アイコンが変わった」と見えて全マーカーの DOM を作り直すことになる
 * (logposeTech/log_web_issues#321)。色 × 番号の組み合わせは車両色パレットと
 * 停車地数で頭打ちになるので、貯めても増え続けない。
 *
 * Why not 生成した DOM 要素を渡す: DivIcon は html に Element を渡せるが、
 * 同じ Element は 1 箇所にしか置けない。同じ色・同じ番号のマーカーは別車両で
 * 同時に出るので、使い回しと両立しない。
 *
 * 非選択を薄くする処理はアイコン側では行わない（opacity で出し分ける。
 * 詳細は mapMarkerStyles）。
 */
const iconCache = new Map<string, Leaflet.DivIcon>();
function numberedIcon(color: string, n: number): Leaflet.DivIcon {
  const key = `${color}|${n}`;
  const cached = iconCache.get(key);
  if (cached) return cached;

  const html = `<div style="background:${cssColor(color)};width:22px;height:22px;border-radius:50%;color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;border:2px solid #fff;">${n}</div>`;
  const icon = Leaflet.divIcon({ className: 'map-vehicle-pin', iconAnchor: [11, 11], html });
  iconCache.set(key, icon);
  return icon;
}

type RouteHandlers = ReturnType<typeof buildRouteMarkerEventHandlers>;

const handlerSlotOf = (marker: Leaflet.Marker): { eventHandlers?: RouteHandlers } =>
  marker.options as { eventHandlers?: RouteHandlers };

/** 1 本の番号マーカーについて、いま反映済みの内容。差分判定に使う */
type ManagedStop = {
  marker: Leaflet.Marker;
  vm: MapRouteMarkerVM;
  color: string;
  opacity: number;
};

/**
 * 配車済みの番号マーカー。
 *
 * Why not 宣言的な `<Marker>`: 番号マーカーはクラスターに入れていないので追加・削除は
 * `map.addLayer` で済むが、**再描画のたびに全マーカーが更新される**のが重い。
 * `numberedIcon(color, seq)` は毎レンダー新しい DivIcon を作るため react-leaflet の
 * `updateMarker` が `setIcon`（アイコン DOM の作り直し）を全マーカーで呼び、
 * `useEventHandlers` (@react-leaflet/core/lib/events.js) も毎レンダー全マーカーの
 * `off`/`on` を掛け直す。車両を 1 台選ぶだけでこれが走っていた
 * （合成 2,000 本で 1 クリック 444ms を実測。logposeTech/log_web_issues#321）。
 *
 * ここでは停車地ごとに 1 本の L.Marker を持ち回り、変わったものだけ触る。
 */
const VehicleRouteMarkers = ({
  routes,
  kinds,
  selectedRowIds,
  hasSelection,
  onMarkerRowClick,
  onMarkerClick,
  onMarkerRowDeselect,
}: Props) => {
  const map = useMap();
  const groupRef = useRef<Leaflet.LayerGroup | null>(null);
  const stopsRef = useRef(new Map<string, ManagedStop>());

  // 呼び出し側のハンドラは毎レンダー別の関数になる。leaflet へ直接張ると
  // マーカー数ぶんの off/on が再描画のたびに走るので、ref 越しに最新を読む
  const handlersRef = useRef({ onMarkerRowClick, onMarkerClick, onMarkerRowDeselect });
  handlersRef.current = { onMarkerRowClick, onMarkerClick, onMarkerRowDeselect };

  useEffect(() => {
    const group = Leaflet.layerGroup();
    map.addLayer(group);
    groupRef.current = group;

    const stops = stopsRef.current;
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
      stops.clear();
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    const stops = stopsRef.current;

    const buildHandlers = (rowId: string, vm: MapRouteMarkerVM): RouteHandlers =>
      buildRouteMarkerEventHandlers(rowId, vm, {
        onMarkerRowClick: (id) => handlersRef.current.onMarkerRowClick(id),
        onMarkerClick: (id, marker) => handlersRef.current.onMarkerClick?.(id, marker),
        onMarkerRowDeselect: (id) => handlersRef.current.onMarkerRowDeselect?.(id),
      });

    // 出す停車地を先に決める。key は宣言的だったころの React key と同じ組み立て
    const wanted: { key: string; rowId: string; color: string; vm: MapRouteMarkerVM }[] = [];
    routes.forEach((route) => {
      route.markers.forEach((vm) => {
        const shown = vm.kind === 'loading' ? showsLoading(kinds) : showsUnloading(kinds);
        if (!shown) return;
        wanted.push({ key: `${route.rowId}-${vm.seq}`, rowId: route.rowId, color: route.color, vm });
      });
    });

    const nextKeys = new Set(wanted.map((w) => w.key));
    stops.forEach((stop, key) => {
      if (nextKeys.has(key)) return;
      group.removeLayer(stop.marker);
      stops.delete(key);
    });

    wanted.forEach(({ key, rowId, color, vm }) => {
      const opacity = markerOpacity({ hasSelection, isSelected: selectedRowIds.has(rowId) });
      const stop = stops.get(key);

      if (stop) {
        if (stop.vm !== vm) {
          handlerSlotOf(stop.marker).eventHandlers = buildHandlers(rowId, vm);
          const at = stop.marker.getLatLng();
          if (at.lat !== vm.position[0] || at.lng !== vm.position[1]) stop.marker.setLatLng(vm.position);
          stop.vm = vm;
        }
        // 色は画面から変えられる（VehicleColorPickerPopover）。変わったときだけ差し替える
        if (stop.color !== color) {
          stop.marker.setIcon(numberedIcon(color, vm.seq));
          stop.color = color;
        }
        if (stop.opacity !== opacity) {
          stop.marker.setOpacity(opacity);
          stop.opacity = opacity;
        }
        return;
      }

      const marker = Leaflet.marker(vm.position, { icon: numberedIcon(color, vm.seq), opacity });
      handlerSlotOf(marker).eventHandlers = buildHandlers(rowId, vm);
      // 読む先を options.eventHandlers に寄せると、ハンドラが差し替わっても
      // リスナを張り直さずに済む（未割当ピンと同じ作り）
      marker.on('click', () => handlerSlotOf(marker).eventHandlers?.click());
      marker.on('contextmenu', () => handlerSlotOf(marker).eventHandlers?.contextmenu());
      stops.set(key, { marker, vm, color, opacity });
      group.addLayer(marker);
    });
  }, [map, routes, kinds, selectedRowIds, hasSelection]);

  return null;
};

export default VehicleRouteMarkers;
